import { useState, useMemo } from 'react';
import {
  LayoutTemplate, Search, RefreshCw, AlertCircle, Loader2,
  Trash2, Pencil, Clock, CheckCircle2, X, FileText,
} from 'lucide-react';
import { PageContainer } from 'components/ui/page-container';
import PageHeader from 'components/ui/PageHeader';
import { toast } from 'components/ui/toast';
import { useListTemplates, useMoveTemplateToTrash, useUpdateTemplate, useTemplate } from './hooks';
import type { ITemplateMeta } from './api';

const ACCENT = 'bg-gradient-to-r from-violet-500 to-indigo-500';
const ICON_BG = 'bg-violet-50 dark:bg-violet-900/20';
const ICON_CLR = 'text-violet-600 dark:text-violet-400';
const GRADIENT = 'from-violet-500 to-indigo-600';

// ─── Helpers ─────────────────────────────────────────────────────────────────
function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return '—';
  }
}

function getDate(t: ITemplateMeta): string {
  return t.modifiedDate ?? t.updated_at ?? t.createdDate ?? t.created_at ?? '';
}

// Real templates = rows where id is not null
function isReal(t: ITemplateMeta): boolean {
  return t.id != null && t.name != null;
}

// ─── Update Modal ─────────────────────────────────────────────────────────────
function UpdateModal({
  template,
  onClose,
}: {
  template: ITemplateMeta;
  onClose: () => void;
}) {
  const [newName, setNewName] = useState(template.name ?? '');
  const [tagName, setTagName] = useState((template.tag ?? template.tag_name) ?? '');
  const { data: loaded, isLoading: loadingDesign } = useTemplate(template.id);
  const { mutateAsync: updateTemplate, isPending } = useUpdateTemplate();

  const handleSubmit = async () => {
    if (!newName.trim()) return;
    if (!loaded) {
      toast.error('Template data not loaded yet — please wait.');
      return;
    }
    try {
      await updateTemplate({
        id: template.id,
        name: newName.trim(),
        design: loaded.design,
        tagName: tagName.trim(),
      });
      toast.success('Template updated successfully!');
      onClose();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Update failed';
      toast.error(msg);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div
        className="bg-surface dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden"
        style={{ border: '1px solid rgba(108,59,255,0.18)' }}
      >
        {/* Header */}
        <div className={`bg-gradient-to-r ${GRADIENT} px-5 py-4 flex items-center justify-between`}>
          <div>
            <h3 className="text-sm font-bold text-white">Update Template</h3>
            <p className="text-micro text-white/65 mt-0.5 truncate max-w-[220px]">{template.name}</p>
          </div>
          <button aria-label="Close"
            onClick={onClose}
            className="w-7 h-7 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
          >
            <X className="w-3.5 h-3.5 text-white" />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-4">
          {/* Loading design */}
          {loadingDesign && (
            <div className="flex items-center gap-2 text-xs text-content-subtle dark:text-gray-500">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Loading design data…
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-[11px] font-semibold text-content-muted dark:text-gray-400 uppercase tracking-wider mb-1.5">
              Template Name
            </label>
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); if (e.key === 'Escape') onClose(); }}
              placeholder="Template name"
              className="w-full px-3.5 py-2.5 rounded-xl border border-line dark:border-dark-600
                bg-gray-50 dark:bg-dark-900 text-sm text-content dark:text-white
                placeholder-gray-400 dark:placeholder-dark-500
                focus:outline-none focus:ring-2 focus:ring-violet-400/30 focus:border-violet-400 transition-all"
            />
          </div>

          {/* Tag */}
          <div>
            <label className="block text-[11px] font-semibold text-content-muted dark:text-gray-400 uppercase tracking-wider mb-1.5">
              Tag <span className="normal-case font-normal">(optional)</span>
            </label>
            <input
              value={tagName}
              onChange={(e) => setTagName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); if (e.key === 'Escape') onClose(); }}
              placeholder="e.g. marketing"
              className="w-full px-3.5 py-2.5 rounded-xl border border-line dark:border-dark-600
                bg-gray-50 dark:bg-dark-900 text-sm text-content dark:text-white
                placeholder-gray-400 dark:placeholder-dark-500
                focus:outline-none focus:ring-2 focus:ring-violet-400/30 focus:border-violet-400 transition-all"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2.5 justify-end pt-1">
            <button
              onClick={onClose}
              disabled={isPending}
              className="px-4 py-2 rounded-xl border border-line dark:border-dark-600
                bg-surface dark:bg-dark-900 text-sm font-semibold text-content-secondary dark:text-gray-400
                hover:bg-gray-50 dark:hover:bg-dark-800 cursor-pointer transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isPending || loadingDesign || !newName.trim()}
              className={`inline-flex items-center gap-2 px-5 py-2 rounded-xl border-0 text-sm font-bold
                cursor-pointer transition-all bg-gradient-to-r ${GRADIENT}
                text-white shadow-sm hover:opacity-90 hover:shadow-md
                disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {isPending
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</>
                : <><CheckCircle2 className="w-3.5 h-3.5" /> Save Changes</>
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Confirm Trash Modal ──────────────────────────────────────────────────────
function ConfirmTrashModal({
  template,
  onConfirm,
  onCancel,
  isPending,
}: {
  template: ITemplateMeta;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div
        className="bg-surface dark:bg-dark-800 rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6 flex flex-col gap-4"
        style={{ border: '1px solid rgba(239,68,68,0.2)' }}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
            <Trash2 className="w-5 h-5 text-red-500 dark:text-red-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-content dark:text-white">Move to Trash?</h3>
            <p className="text-xs text-content-muted dark:text-gray-400 mt-0.5 leading-relaxed">
              <span className="font-semibold text-gray-700 dark:text-gray-300">"{template.name}"</span> will be moved to the trash and can be restored later.
            </p>
          </div>
        </div>
        <div className="flex gap-2.5 justify-end">
          <button
            onClick={onCancel}
            disabled={isPending}
            className="px-4 py-2 rounded-xl border border-line dark:border-dark-600
              bg-surface dark:bg-dark-900 text-sm font-semibold text-content-secondary dark:text-gray-400
              hover:bg-gray-50 dark:hover:bg-dark-800 cursor-pointer transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-xl border-0 text-sm font-bold
              cursor-pointer transition-all bg-red-500 hover:bg-red-600
              text-white shadow-sm hover:shadow-md
              disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isPending
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Moving…</>
              : <><Trash2 className="w-3.5 h-3.5" /> Move to Trash</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Skeleton Row ─────────────────────────────────────────────────────────────
function SkeletonRow() {
  return (
    <tr className="animate-pulse block md:table-row mb-4 md:mb-0 rounded-2xl border border-line dark:border-dark-700 bg-surface dark:bg-dark-800 overflow-hidden shadow-sm md:rounded-none md:border-b md:border-line-subtle md:dark:border-dark-700 md:bg-transparent md:shadow-none">
      <td className="px-4 py-3.5 block md:table-cell"><div className="h-4 w-40 rounded bg-gray-100 dark:bg-dark-700" /></td>
      <td className="px-4 py-3.5 block md:table-cell border-t border-gray-50 dark:border-dark-700/50 md:border-0"><div className="h-4 w-20 rounded bg-gray-100 dark:bg-dark-700 md:ml-0 ml-auto" /></td>
      <td className="px-4 py-3.5 block md:table-cell border-t border-gray-50 dark:border-dark-700/50 md:border-0"><div className="h-4 w-28 rounded bg-gray-100 dark:bg-dark-700 mt-2 md:mt-0" /></td>
      <td className="px-4 py-3.5 block md:table-cell border-t border-gray-50 dark:border-dark-700/50 md:border-0"><div className="h-4 w-24 rounded bg-gray-100 dark:bg-dark-700 md:ml-0 ml-auto" /></td>
      <td className="px-4 py-3.5 text-right block md:table-cell border-t border-gray-50 dark:border-dark-700/50 md:border-0 bg-gray-50/50 dark:bg-dark-900/20 md:bg-transparent"><div className="h-7 w-20 rounded-lg bg-gray-100 dark:bg-dark-700 ml-auto" /></td>
    </tr>
  );
}

// ─── Template Row ─────────────────────────────────────────────────────────────
function TemplateRow({
  template,
  onEdit,
  onTrash,
  trashPending,
}: {
  template: ITemplateMeta;
  onEdit: () => void;
  onTrash: () => void;
  trashPending: boolean;
}) {
  return (
    <tr className="group hover:bg-violet-50/40 dark:hover:bg-violet-900/10 transition-colors block md:table-row mb-4 md:mb-0 rounded-2xl border border-line dark:border-dark-700 bg-surface dark:bg-dark-800 overflow-hidden shadow-sm md:rounded-none md:border-b md:border-line-subtle md:dark:border-dark-700 md:bg-transparent md:shadow-none">
      {/* Name */}
      <td className="px-4 py-3.5 block md:table-cell">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${GRADIENT} flex items-center justify-center flex-shrink-0 shadow-sm`}>
            <FileText className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-sm font-semibold text-content dark:text-white truncate max-w-[200px]">
            {template.name}
          </span>
        </div>
      </td>

      {/* Tag */}
      <td className="px-4 py-3.5 block md:table-cell border-t border-gray-50 dark:border-dark-700/50 md:border-0">
        <div className="flex items-center justify-between md:block">
          <span className="md:hidden text-[11px] font-semibold text-content-muted uppercase tracking-wider">Tag</span>
          {(template.tag ?? template.tag_name) ? (
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${ICON_BG} ${ICON_CLR}`}>
              {template.tag ?? template.tag_name}
            </span>
          ) : (
            <span className="text-[11px] text-gray-300 dark:text-dark-600">—</span>
          )}
        </div>
      </td>

      {/* Description */}
      <td className="px-4 py-3.5 block md:table-cell border-t border-gray-50 dark:border-dark-700/50 md:border-0">
        <div className="flex flex-col md:block gap-1">
          <span className="md:hidden text-[11px] font-semibold text-content-muted uppercase tracking-wider">Description</span>
          <span className="text-xs text-content-muted dark:text-gray-400 truncate max-w-full md:max-w-[180px] block">
            {template.description || '—'}
          </span>
        </div>
      </td>

      {/* Modified Date */}
      <td className="px-4 py-3.5 block md:table-cell border-t border-gray-50 dark:border-dark-700/50 md:border-0">
        <div className="flex items-center justify-between md:block">
          <span className="md:hidden text-[11px] font-semibold text-content-muted uppercase tracking-wider">Modified</span>
          <span className="text-[11px] text-content-subtle dark:text-dark-500 flex items-center gap-1">
            <Clock className="w-3 h-3 flex-shrink-0" />
            {formatDate(getDate(template))}
          </span>
        </div>
      </td>

      {/* Actions */}
      <td className="px-4 py-3.5 text-right block md:table-cell border-t border-gray-50 dark:border-dark-700/50 md:border-0 bg-gray-50/50 dark:bg-dark-900/20 md:bg-transparent">
        <div className="flex items-center gap-1.5 justify-end">
          <button
            onClick={onEdit}
            title="Update template"
            className="inline-flex items-center justify-center gap-1.5 p-1.5 sm:px-3 sm:py-1.5 rounded-lg
              text-xs font-semibold text-violet-600 dark:text-violet-400
              bg-violet-50 dark:bg-violet-900/20 hover:bg-violet-100 dark:hover:bg-violet-900/40
              border border-violet-200/60 dark:border-violet-700/40
              cursor-pointer transition-colors"
          >
            <Pencil className="w-3.5 h-3.5 sm:w-3 sm:h-3" />
            <span className="hidden sm:inline">Update</span>
          </button>
          <button
            onClick={onTrash}
            disabled={trashPending}
            title="Move to trash"
            className="inline-flex items-center justify-center gap-1.5 p-1.5 sm:px-3 sm:py-1.5 rounded-lg
              text-xs font-semibold text-red-500 dark:text-red-400
              bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40
              border border-red-200/60 dark:border-red-700/40
              cursor-pointer transition-colors
              disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {trashPending
              ? <Loader2 className="w-3.5 h-3.5 sm:w-3 sm:h-3 animate-spin" />
              : <Trash2 className="w-3.5 h-3.5 sm:w-3 sm:h-3" />
            }
            <span className="hidden sm:inline">Trash</span>
          </button>
        </div>
      </td>
    </tr>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function TemplateListPage() {
  const { data: templates = [], isLoading, error, refetch } = useListTemplates();
  const { mutateAsync: trashTemplate, isPending: trashPending, variables: trashVars } = useMoveTemplateToTrash();

  const [search, setSearch] = useState('');
  const [editingTemplate, setEditingTemplate] = useState<ITemplateMeta | null>(null);
  const [trashTarget, setTrashTarget] = useState<ITemplateMeta | null>(null);

  const filtered = useMemo(() =>
    templates
      // Skip placeholder rows returned by the API when Total_templates is 0
      .filter((t) => t.id != null && t.name != null)
      .filter((t) =>
        (t.name ?? '').toLowerCase().includes(search.toLowerCase()) ||
        (t.tag ?? t.tag_name ?? '').toLowerCase().includes(search.toLowerCase())
      ),
    [templates, search]
  );

  const handleTrashConfirm = async () => {
    if (!trashTarget) return;
    try {
      await trashTemplate(trashTarget.id);
      toast.success(`"${trashTarget.name}" moved to trash.`);
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to move to trash';
      toast.error(msg);
    } finally {
      setTrashTarget(null);
    }
  };

  const realCount = templates.filter(isReal).length;

  return (
    <>
      {/* ── Update Modal ── */}
      {editingTemplate && (
        <UpdateModal
          template={editingTemplate}
          onClose={() => setEditingTemplate(null)}
        />
      )}

      {/* ── Confirm Trash Modal ── */}
      {trashTarget && (
        <ConfirmTrashModal
          template={trashTarget}
          onConfirm={handleTrashConfirm}
          onCancel={() => setTrashTarget(null)}
          isPending={trashPending && trashVars === trashTarget.id}
        />
      )}

      <PageContainer>
        {/* ── Header ── */}
        <PageHeader
          icon={<LayoutTemplate className="w-5 h-5" />}
          iconBg={ICON_BG}
          iconColor={ICON_CLR}
          accentColor={ACCENT}
          title="Template List"
          subtitle="Manage all saved email templates — update or move to trash"
          badge={realCount > 0 ? `${realCount} template${realCount !== 1 ? 's' : ''}` : undefined}
        />

        {/* ── Error ── */}
        {error && !isLoading && (
          <div className="mt-6 flex flex-col items-center gap-3 py-12 text-center">
            <div className="w-12 h-12 rounded-2xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-red-500 dark:text-red-400" />
            </div>
            <p className="text-sm text-content-secondary dark:text-dark-300 font-medium">Failed to load templates</p>
            <button
              onClick={() => refetch()}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl
                bg-surface dark:bg-dark-800 border border-line dark:border-dark-600
                text-xs font-semibold text-gray-700 dark:text-dark-300
                hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors cursor-pointer"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Retry
            </button>
          </div>
        )}

        {/* ── Table ── */}
        {!error && (
          <div className="mt-4">
            {/* Search */}
            <div className="flex items-center gap-3 mb-4">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-content-subtle pointer-events-none" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search templates…"
                  className="w-full h-9 pl-9 pr-3 text-sm rounded-xl
                    border border-line dark:border-dark-600
                    bg-surface dark:bg-dark-800 text-content dark:text-white
                    placeholder-gray-400 dark:placeholder-dark-500
                    focus:outline-none focus:ring-2 focus:ring-violet-400/30 focus:border-violet-400 transition-all"
                />
              </div>
              {search && (
                <p className="text-xs text-content-subtle dark:text-dark-500">
                  {filtered.length} result{filtered.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>

            {/* Table */}
            <div className="md:rounded-2xl md:border md:border-line md:dark:border-dark-700 md:bg-surface md:dark:bg-dark-800 md:overflow-hidden md:shadow-sm">
              <table className="w-full text-left border-collapse block md:table">
                <thead className="hidden md:table-header-group">
                  <tr className="border-b border-line-subtle dark:border-dark-700 bg-gray-50/60 dark:bg-dark-900/40">
                    <th className="px-4 py-3 text-[11px] font-semibold text-content-muted dark:text-dark-400 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-[11px] font-semibold text-content-muted dark:text-dark-400 uppercase tracking-wider">Tag</th>
                    <th className="px-4 py-3 text-[11px] font-semibold text-content-muted dark:text-dark-400 uppercase tracking-wider">Description</th>
                    <th className="px-4 py-3 text-[11px] font-semibold text-content-muted dark:text-dark-400 uppercase tracking-wider">Modified</th>
                    <th className="px-4 py-3 text-[11px] font-semibold text-content-muted dark:text-dark-400 uppercase tracking-wider text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="block md:table-row-group">
                  {isLoading && Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)}

                  {!isLoading && filtered.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-4 py-16 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${GRADIENT} opacity-20 flex items-center justify-center mx-auto`}>
                            <LayoutTemplate className="w-6 h-6 text-white" />
                          </div>
                          <p className="text-sm text-content-subtle dark:text-dark-500">
                            {search
                              ? <>No templates match "<span className="font-semibold text-content-secondary dark:text-dark-300">{search}</span>"</>
                              : 'No templates found'
                            }
                          </p>
                          {search && (
                            <button
                              onClick={() => setSearch('')}
                              className="text-xs font-semibold text-violet-600 dark:text-violet-400 hover:underline cursor-pointer bg-transparent border-none"
                            >
                              Clear search
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}

                  {!isLoading && filtered.map((template) => (
                    <TemplateRow
                      key={template.id}
                      template={template}
                      onEdit={() => setEditingTemplate(template)}
                      onTrash={() => setTrashTarget(template)}
                      trashPending={trashPending && trashVars === template.id}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer count */}
            {!isLoading && realCount > 0 && (
              <p className="mt-3 text-[11px] text-content-subtle dark:text-dark-500 text-right">
                Showing {filtered.length} of {realCount} template{realCount !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        )}
      </PageContainer>
    </>
  );
}
